import { useRef, useState, useMemo } from 'react';
import {
  Button,
  ButtonWithDropdown,
  Grid,
  useTheme,
} from '@simplywallst/ui-core';
import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import Observer from '@researchgate/react-intersection-observer';
import { intersectorThresholds } from '@/constants/intersectorThresholds';
import { SWS_LEGACY_HOST } from '@/constants';
import Media from 'react-media';
import { ValuationContainer } from '@/components/DeprecatedPortfolio/components/Valuation';
import HoldingsContainer from '@/components/DeprecatedPortfolio/components/Holdings/containers/HoldingsContainer';
import { FutureContainer } from '@/components/DeprecatedPortfolio/components/Future';
import { SummaryContainer } from '@/components/DeprecatedPortfolio/components/Summary';
import { ReturnsContainer } from '@/components/DeprecatedPortfolio/components/Returns';
import { DividendContainer } from '@/components/DeprecatedPortfolio/components/Dividend';
import { DiversificationContainer } from '@/components/DeprecatedPortfolio/components/Diversification';
import { PastContainer } from '@/components/DeprecatedPortfolio/components/Past';
import { NewsContainer } from '@/components/DeprecatedPortfolio/components/News';
import { HealthContainer } from '@/components/DeprecatedPortfolio/components/Health';
import DeprecatedKeyMetric from '@/components/DeprecatedKeyMetric';
import PageAnchor from '@components/PageAnchor';
import {
  PortfolioReportWrapper,
  Provider,
  SectionWrapper,
  HeaderControls,
  Feedback,
  FeedbackContent,
  BannerWrapper,
} from './styled';
import {
  PORTFOLIO_SUMMARY_ANCHOR,
  PORTFOLIO_HOLDINGS_ANCHOR,
  PORTFOLIO_NEWS_ANCHOR,
  PORTFOLIO_RETURNS_ANCHOR,
  PORTFOLIO_DIVERSIFICATION_ANCHOR,
  PORTFOLIO_VALUATION_ANCHOR,
  PORTFOLIO_FUTURE_ANCHOR,
  PORTFOLIO_DIVIDEND_ANCHOR,
  PORTFOLIO_PAST_ANCHOR,
  PORTFOLIO_HEALTH_ANCHOR,
} from '@/constants/anchors';
import {
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
  ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
  ROUTE_MY_PORTFOLIOS,
  ROUTE_DEPRECATED_PORTFOLIO,
} from '@/constants/routes';
import {
  ImageHeader,
  DeprecatedTitle,
  BreadcrumbContainer,
} from '@components/Header';
import Footer from '@components/Footer';
import Breadcrumb from '@components/Breadcrumb';
import { slugify } from '@utilities/slugify';
import { LoadableChartDefinitionsProvider } from '@components/ChartDefinitionsContext';
import { useTracking } from 'react-tracking';
import {
  SharePortfolioModal,
  DeletePortfolioModal,
} from '@/components/DeprecatedPortfolio';
import useCapacitorDevicePlugin from '@/hooks/useCapacitorDevicePlugin';
import { useTypeformDrawer } from '@/hooks/useTypeformDrawer';
import { useQueryClient } from '@tanstack/react-query';
import { queryKey as userPortfoliosQueryKey } from '@/components/DeprecatedPortfolios/components/UserPortfolios/query/getUserPortfolios';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import { getMarketCoverFromCurrency } from '@/utilities/coverImages';
import { DeprecatedNewPortfolioBanner } from '@/components/DeprecatedPortfolios/components/UserPortfolios/DeprecatedNewPortfolioBanner';

import type { CurrencyISO } from '@/constants/currencies';

const SHARE_ACTION = 'SHARE_ACTION';
const REFRESH_ACTION = 'REFRESH_ACTION';
const SETTINGS_ACTION = 'SETTINGS_ACTION';
const SET_DEFAULT_ACTION = 'SET_DEFAULT_ACTION';
const DELETE_ACTION = 'DELETE_ACTION';

const moreActions = [
  { label: 'Refresh', value: REFRESH_ACTION },
  { label: 'Share', value: SHARE_ACTION },
  { label: 'Settings', value: SETTINGS_ACTION },
  { label: 'Set as Default', value: SET_DEFAULT_ACTION },
  { label: 'Delete', value: DELETE_ACTION },
];

interface Props {
  id: string | null;
  isEditable?: boolean;
  name: string | null;
  provider?: string | null;
  totalValueFormatted: string;
  currencyISO?: CurrencyISO;
  currentHoldingCount: number;
  isWatchlist: boolean;
  secretCode?: string | null;
  onIntersectionChange: (e: IntersectionObserverEntry, anchor: string) => void;
  handlePortfolioRefresh: (id: string) => void;
  redirectToEditTransactions: (args: { id: string; name: string }) => void;
  handleShowEditPortfolio: () => void;
  handleDeletePortfolio: (args: {
    id: string;
    redirectToList: boolean;
  }) => void;
}

const PortfolioReport = ({
  id,
  name,
  provider,
  totalValueFormatted,
  currencyISO,
  currentHoldingCount,
  isEditable,
  isWatchlist,
  secretCode,
  onIntersectionChange,
  redirectToEditTransactions,
  handlePortfolioRefresh,
  handleShowEditPortfolio,
  handleDeletePortfolio,
}: Props) => {
  const theme = useTheme();
  const { trackEvent } = useTracking();
  const queryClient = useQueryClient();
  const tooltipAnchorRef = useRef<null | HTMLDivElement>(null);
  const [shareModalShow, setShareModalShow] = useState(false);
  const [deletePortfolioModalShow, setDeletePortfolioModalShow] =
    useState(false);
  const { isNativeApp } = useCapacitorDevicePlugin();
  const makeTypeformPopup = useTypeformDrawer('Hk37XBaI');

  const portfolioBreadcrumbLink = useMemo(() => {
    return [
      ...(!isEditable
        ? [
            {
              name: 'Discover',
              dispatch: {
                type: ROUTE_DISCOVER_INVESTING_IDEAS,
              },
            },
            {
              name: 'Sample Portfolios',
              dispatch: {
                type: ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
              },
            },
          ]
        : [
            {
              name: 'Portfolio',
              dispatch: { type: ROUTE_MY_PORTFOLIOS },
            },
            {
              name: 'My Portfolios',
              dispatch: { type: ROUTE_MY_PORTFOLIOS },
            },
          ]),
      {
        name: name as string,
        dispatch: {
          type: isEditable
            ? ROUTE_DEPRECATED_PUBLIC_PORTFOLIO
            : ROUTE_DEPRECATED_PORTFOLIO,
          payload: {
            portfolioId: id,
            name: slugify(name as string),
          },
        },
      },
    ];
  }, [name, id, isEditable]);

  const coverImageSrc = useCloudflareImage({
    src: getMarketCoverFromCurrency(currencyISO),
    height: 320,
    width: 885,
    fit: 'cover',
  });

  const responsiveImage = getResponsiveImage({
    src: getMarketCoverFromCurrency(currencyISO),
    breakpoints: [885, 768, 425],
  });
  const handleEditTransactions = () => {
    trackEvent({
      action: 'click',
      subject: 'edit',
    });
    redirectToEditTransactions({
      id: id as string,
      name: name as string,
    });
  };

  const handleMoreActionSelect = (value: string, label: string) => {
    if (!id) return;
    trackEvent({
      action: 'click',
      subject: label,
    });
    switch (value) {
      case REFRESH_ACTION:
        handlePortfolioRefresh(id);
        break;
      case SHARE_ACTION:
        setShareModalShow(true);
        break;
      case SETTINGS_ACTION:
        handleShowEditPortfolio();
        break;
      case DELETE_ACTION:
        setDeletePortfolioModalShow(true);
        break;
    }
  };

  return (
    <LoadableChartDefinitionsProvider>
      <PortfolioReportWrapper>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_HOLDINGS_ANCHOR);
          }}
        >
          <div>
            <PageAnchor id={PORTFOLIO_HOLDINGS_ANCHOR} />

            <BannerWrapper>
              <DeprecatedNewPortfolioBanner
                v1PortfolioId={id}
                holdingCount={currentHoldingCount}
              />
            </BannerWrapper>

            <ImageHeader
              imageSource={coverImageSrc}
              imageSourceSet={responsiveImage.srcSet}
            >
              <BreadcrumbContainer>
                <Breadcrumb list={portfolioBreadcrumbLink} />
              </BreadcrumbContainer>
              <DeprecatedTitle data-cy-id="portfolio-header-title">
                {name}
              </DeprecatedTitle>
              <Grid templateRows="80px auto">
                {isEditable && id && (
                  <HeaderControls>
                    <Media query={{ minWidth: theme.lgMin }}>
                      {(matches) =>
                        matches &&
                        !isNativeApp && (
                          <Button
                            data-cy-id="portfolio-sidebar-edit-pf"
                            styleType="louderLarge"
                            marginRight
                            onClick={handleEditTransactions}
                          >
                            Edit Portfolio
                          </Button>
                        )
                      }
                    </Media>
                    <ButtonWithDropdown
                      data-cy-id="portfolio-more-actions"
                      styleType="louderLarge"
                      floatLeft
                      options={moreActions}
                      onSelect={handleMoreActionSelect}
                    >
                      <>
                        <span>More Actions </span>
                        <MoreHorizontalSolid />
                      </>
                    </ButtonWithDropdown>
                  </HeaderControls>
                )}
                {!isWatchlist && (
                  <div>
                    <DeprecatedKeyMetric
                      size="small"
                      title="Market Value"
                      metric={
                        totalValueFormatted && totalValueFormatted !== '0.00'
                          ? totalValueFormatted
                          : 'N/A'
                      }
                    />
                  </div>
                )}
              </Grid>
            </ImageHeader>
            {shareModalShow && (
              <SharePortfolioModal
                secretCode={secretCode as string}
                portfolioId={id as string}
                portfolioName={name as string}
                onClose={() => {
                  setShareModalShow(false);
                }}
                onConfirm={() => {
                  setShareModalShow(false);
                }}
              />
            )}
            {deletePortfolioModalShow && id && (
              <DeletePortfolioModal
                onClose={() => {
                  setDeletePortfolioModalShow(false);
                }}
                onConfirm={() => {
                  queryClient.invalidateQueries({
                    queryKey: [userPortfoliosQueryKey],
                  });
                  handleDeletePortfolio({
                    id,
                    redirectToList: true,
                  });
                }}
              />
            )}
            {provider && (
              <Provider>
                <span>This portfolio is linked to </span>
                <img
                  src={`${SWS_LEGACY_HOST}/build/images/partners/dark/${provider}.png`}
                  alt="provider"
                />
              </Provider>
            )}
            <SectionWrapper>
              <PageAnchor id={PORTFOLIO_HOLDINGS_ANCHOR} />
              <HoldingsContainer sectionNumber={1} />
              <Feedback
                gap="2px"
                templateColumns="24px auto"
                alignItems="start"
                alignItemsMd="center"
              >
                <InformationSolid fill={theme.color.whiteRGBA(0.5)} />
                <FeedbackContent>
                  Have your say and help us build a New Portfolio feature that
                  works for you.{' '}
                  <Button
                    styleType="strongLink"
                    display="inline"
                    onClick={makeTypeformPopup}
                  >
                    Take the survey
                  </Button>
                  .
                </FeedbackContent>
              </Feedback>
            </SectionWrapper>
          </div>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_SUMMARY_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_SUMMARY_ANCHOR} />
            <SummaryContainer sectionNumber={2} />
          </SectionWrapper>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_NEWS_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_NEWS_ANCHOR} />
            <NewsContainer sectionNumber={3} />
          </SectionWrapper>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_RETURNS_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_RETURNS_ANCHOR} />
            <ReturnsContainer sectionNumber={4} />
          </SectionWrapper>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_DIVERSIFICATION_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_DIVERSIFICATION_ANCHOR} />
            <DiversificationContainer sectionNumber={5} />
          </SectionWrapper>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_VALUATION_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_VALUATION_ANCHOR} />
            <ValuationContainer sectionNumber={6} />
          </SectionWrapper>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_FUTURE_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_FUTURE_ANCHOR} />
            <FutureContainer sectionNumber={7} />
          </SectionWrapper>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_PAST_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_PAST_ANCHOR} />
            <PastContainer sectionNumber={8} />
          </SectionWrapper>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_HEALTH_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_HEALTH_ANCHOR} />
            <HealthContainer sectionNumber={9} />
          </SectionWrapper>
        </Observer>
        <Observer
          threshold={intersectorThresholds}
          onChange={(e) => {
            onIntersectionChange(e, PORTFOLIO_DIVIDEND_ANCHOR);
          }}
        >
          <SectionWrapper>
            <PageAnchor id={PORTFOLIO_DIVIDEND_ANCHOR} />
            <DividendContainer sectionNumber={10} />
          </SectionWrapper>
        </Observer>
        <Media query={`(min-width: ${theme.lgMin})`}>
          {(matches) => (matches ? <Footer /> : null)}
        </Media>
        <div id="company-tooltip-container" ref={tooltipAnchorRef} />
      </PortfolioReportWrapper>
    </LoadableChartDefinitionsProvider>
  );
};

export default PortfolioReport;
